import { authFetch } from './auth';
import type { SalaryMessage } from '../types/salaryNegotiation';
import type { SalaryNegotiationConfig } from './salaryAIService';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://mmbang.site';

// 谈薪对话语音合成参数
export interface SalaryChatTTSParams {
  dialogueHistory: SalaryMessage[];
  salaryConfig: SalaryNegotiationConfig;
  voice?: string;
  audio: string; // base64音频
  onAudioData?: (base64Audio: string) => void;
  onComplete?: (result: {
    userText: string;
    hrText: string;
    isFinal: boolean;
    offerAmount?: number;
  }) => void;
  onError?: (error: Error) => void;
}

/**
 * 谈薪对话语音合成整合接口
 * 流程：语音识别 -> 生成HR回复 -> 语音合成
 */
export async function salaryChatWithTTS(
  params: SalaryChatTTSParams,
): Promise<void> {
  const {
    dialogueHistory,
    salaryConfig,
    voice = 'Maia',
    audio,
    onAudioData,
    onComplete,
    onError,
  } = params;

  try {
    const response = await authFetch(`${API_BASE_URL}/api/salary/chat-tts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        dialogueHistory,
        salaryConfig,
        voice,
        audio,
      }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || `请求失败: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('没有返回响应流');
    }

    // 读取SSE流
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let sseBuffer = '';
    let userText = '';
    let hrText = '';
    let isFinal = false;
    let offerAmount: number | undefined;

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        break;
      }

      sseBuffer += decoder.decode(value, { stream: true });

      const events = sseBuffer.split('\n\n');
      sseBuffer = events.pop() || '';

      for (const event of events) {
        if (!event.trim() || event.startsWith(':')) continue;

        const lines = event.split('\n').filter((line) => line.trim());
        for (const line of lines) {
          if (!line.startsWith('data:')) continue;

          try {
            const jsonStr = line.slice(5).trim();

            if (jsonStr === '[DONE]') {
              continue;
            }

            const sseData = JSON.parse(jsonStr);

            // 检测 complete 事件
            if (sseData.type === 'complete') {
              userText = sseData.userText || '';
              hrText = sseData.hrText || '';
              isFinal = sseData.isFinal || false;
              offerAmount = sseData.offerAmount;
              onComplete?.({ userText, hrText, isFinal, offerAmount });
              continue;
            }

            // 解析阿里TTS音频数据
            let base64Audio = '';

            if (sseData.payload?.audio) {
              base64Audio = sseData.payload.audio;
            } else if (sseData.output?.audio?.data) {
              base64Audio = sseData.output.audio.data;
            } else if (sseData.delta) {
              base64Audio = sseData.delta;
            }

            if (base64Audio) {
              onAudioData?.(base64Audio);
            }
          } catch (parseError) {
            // 解析失败，忽略该行
            console.warn('SSE parse error:', parseError);
          }
        }
      }
    }
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    onError?.(err);
    throw err;
  }
}
